import { ENV } from '@findigs/api/src/api';
import { serializeForScript } from '@findigs/helpers/src/helpers/serialize-for-script';
import { useHydrated } from '@findigs/helpers/src/hooks/use-hydrated';
import { IsDevUserContext } from '@findigs/helpers/src/hooks/use-is-dev-user';
import { FeatureFlagsContext } from '@findigs/helpers/src/integrations/posthog/use-feature-flags';
import { usePostHogInitClient } from '@findigs/helpers/src/integrations/posthog/use-posthog-init-client';
import { usePylonPreloadScript } from '@findigs/helpers/src/integrations/pylon/pylon';
import { PylonContextProvider } from '@findigs/helpers/src/integrations/pylon/pylon-context';
import { ToastProvider } from '@findigs/ui-kit/src/components/toast';
import { cssBundleHref } from '@remix-run/css-bundle';
import type { LinksFunction, SerializeFrom } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
} from '@remix-run/react';
import { useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import { ClientOnly } from '~/client-only';
import type { FooterProps } from '~/components/footer';
import { Footer } from '~/components/footer';
import { Navbar } from '~/components/navbar/navbar';
import type { NavbarPureProps } from '~/components/navbar/navbar-pure';
import { RootErrorBoundary } from '~/error-boundary.root';
import { useSetJWTLocalStorage } from '~/hooks/use-set-jwt-local-storage';
import { useRudderStackIntegrationRoot } from '~/integrations/rudderstack';
import { getLinks } from '~/links';
import { removeOldHead } from '~/remove-old-head';
import { PortfolioContextProvider } from './components/context/portfolio-context';
import { UserContextProvider } from './components/context/user-context';
import type { loader } from './root.server';

export { loader } from './root.server';

export type WindowEnv = SerializeFrom<typeof loader>['ENV'];

export const links: LinksFunction = () => getLinks(cssBundleHref);

function useRouteMatchData() {
  const matches = useMatches();

  let navbar: boolean | NavbarPureProps<any>['variant'] | undefined = undefined;
  let navbarLogoHref: string | false | undefined = undefined;
  let footer: boolean | FooterProps['variant'] | undefined = undefined;
  let background: NonNullable<RouteHandle>['background'] = undefined;

  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];

    navbar ??= match.handle?.navbar;
    footer ??= match.handle?.footer;
    navbarLogoHref ??= match.handle?.navbarLogoHref;
    background ??= match.handle?.background;
  }

  return {
    navbar,
    footer,
    background,
    navbarLogoHref: (navbarLogoHref ?? '/') || undefined,
  };
}

export function Head({ title }: { title?: string }) {
  const [renderHead, setRenderHead] = useState(false);
  const hydrated = useHydrated();
  useEffect(() => {
    if (!hydrated) return;
    if (!renderHead) {
      // trigger re-render so we can remove the old head
      setRenderHead(true);
      return;
    }
    removeOldHead(document.head);
  }, [renderHead, hydrated]);

  return (
    <>
      {title && <title>{title}</title>}
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width,initial-scale=1" />
      <Meta />
      <Links />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV=${serializeForScript(ENV)}`,
        }}
      />
    </>
  );
}

function AppBody({
  title,
  loaderData,
  children,
}: PropsWithChildren<{
  title?: string;
  loaderData: SerializeFrom<typeof loader> | null;
}>) {
  const { navbar, footer, navbarLogoHref, background } = useRouteMatchData();
  const { navbarData } = loaderData ?? {};

  return (
    <>
      <ClientOnly>
        {() => createPortal(<Head title={title} />, document.head)}
      </ClientOnly>

      <div id="app-body" className={background}>
        <ToastProvider>
          {navbar !== false && (
            <Navbar
              loaderData={navbarData ?? null}
              logoHref={navbarLogoHref}
              variant={navbar === true ? undefined : navbar}
            />
          )}
          {children ? children : <Outlet />}
          {footer !== false && (
            <Footer variant={footer === true ? undefined : footer} />
          )}
        </ToastProvider>
      </div>

      <ScrollRestoration />
      <Scripts />
    </>
  );
}

export default function App({ title }: { title?: string }) {
  const loaderData = useLoaderData<typeof loader>();
  const {
    ENV,
    jwt,
    featureFlags,
    userId,
    devUser,
    user,
    users,
    userRoles,
    rightsList,
    allPylonPortalAccess,
    portfolioId,
    portfolioInfo,
    portfolioList,
    limitedPortfoliosList,
    portfolioConfig,
  } = loaderData;

  usePostHogInitClient({
    key: ENV.POSTHOG_PUBLISHABLE_KEY,
    distinctId: userId,
  });

  useSetJWTLocalStorage(jwt);
  useRudderStackIntegrationRoot({ userId });

  const preloaded = usePylonPreloadScript(ENV.PYLON_CHAT_APP_ID);

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>
      <UserContextProvider
        isDevUser={devUser}
        user={user}
        userRole={user?.user_role}
        users={users}
        userRoles={userRoles}
        rightsList={rightsList}
      >
        <IsDevUserContext.Provider value={devUser}>
          <PortfolioContextProvider
            portfolioId={portfolioId}
            portfolioInfo={portfolioInfo}
            portfolioConfig={portfolioConfig}
            portfolioList={portfolioList ?? []}
            limitedPortfoliosList={limitedPortfoliosList ?? []}
          >
            <PylonContextProvider
              preloaded={preloaded}
              user={user}
              userRole={user?.user_role}
              portfolio={{
                id: portfolioId,
                display_name: portfolioInfo?.display_name ?? '',
                is_cra_portfolio: portfolioInfo?.is_cra_portfolio ?? false,
              }}
              portfolioConfig={portfolioConfig}
              allPylonPortalAccess={allPylonPortalAccess}
            >
              <AppBody title={title} loaderData={loaderData} />
            </PylonContextProvider>
          </PortfolioContextProvider>
        </IsDevUserContext.Provider>
      </UserContextProvider>
    </FeatureFlagsContext.Provider>
  );
}

export function ErrorBoundary() {
  return (
    <AppBody title="Oops, something went wrong | Findigs" loaderData={null}>
      <RootErrorBoundary />
    </AppBody>
  );
}
